import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate } from '../../lib/formatDate';
import { getDocuments, documentUrl, listOf } from './vendorsApi';
import { DOC_TYPE_LABEL, FIELD_LABEL, odooDateTime } from './vendorLabels';
import { Skeleton, InlineError, EmptyValue } from './VendorBits';

// Loads once per mount and again when `reloadToken` bumps. A failed Odoo read is
// its own state, never an empty list.
export function useOdooList(fetcher, key, reloadToken) {
  const [state, setState] = useState({ status: 'loading', items: [] });
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  useEffect(() => {
    setState((s) => ({ status: 'loading', items: s.items }));
    fetcher()
      .then((d) => { if (live.current) setState({ status: 'ready', items: listOf(d, key) }); })
      .catch(() => { if (live.current) setState({ status: 'error', items: [] }); });
    // fetcher identity is stable per vendor via the caller's useCallback.
  }, [fetcher, key, reloadToken]);
  return state;
}

function DocIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

/** Contracts, NDAs and certificates uploaded through Nexus (other Odoo attachments never show). */
export function DocumentsSection({ vendorId, reloadToken = 0, action = null }) {
  const fetcher = useCallback(() => getDocuments(vendorId), [vendorId]);
  const { status, items } = useOdooList(fetcher, 'documents', reloadToken);
  return (
    <section className="vd-panel" aria-labelledby="vd-docs-title">
      <header className="vd-panel-head">
        <h2 id="vd-docs-title" className="vd-panel-title">Documents</h2>
        {action}
      </header>
      {status === 'loading' && items.length === 0 && <Skeleton rows={2} label="Loading documents" />}
      {status === 'error' && <InlineError message="Couldn't reach Odoo for documents right now." />}
      {status === 'ready' && items.length === 0 && (
        <p className="vd-muted">No contract, NDA or certificate uploaded through Nexus yet.</p>
      )}
      {items.length > 0 && (
        <ul className="vd-docs">
          {items.map((d) => (
            <li key={d.id} className="vd-doc">
              <span className="vd-doc-icon"><DocIcon /></span>
              <div className="vd-doc-text">
                <span className="vd-doc-type">{DOC_TYPE_LABEL[d.doc_type] || 'Document'}</span>
                <span className="vd-doc-meta">
                  {d.name} · {formatDate(odooDateTime(d.uploaded_at || d.created_at || d.create_date))}
                </span>
              </div>
              <a className="vd-text-btn" href={documentUrl(vendorId, d.id)} download aria-label={`Download ${d.name}`}>Download</a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const orDash = (v) => (v === null || v === undefined || v === '' ? <EmptyValue /> : v);
const websiteHref = (w) => (/^https?:\/\//i.test(w) ? w : `https://${w}`);

/** Contact, legal and identity facts. Contact values are links: that is what people come to copy. */
export function FactsSection({ vendor }) {
  const address = [vendor.street, vendor.street2, vendor.city, vendor.state_name, vendor.zip, vendor.country_name]
    .filter(Boolean).join(', ');
  const rows = [
    ['Contact', [
      [FIELD_LABEL.email, vendor.email ? <a href={`mailto:${vendor.email}`}>{vendor.email}</a> : null],
      [FIELD_LABEL.phone, vendor.phone ? <a href={`tel:${vendor.phone.replace(/\s+/g, '')}`}>{vendor.phone}</a> : null],
      [FIELD_LABEL.website, vendor.website ? <a href={websiteHref(vendor.website)} target="_blank" rel="noopener noreferrer">{vendor.website}</a> : null],
      ['Address', address || null],
    ]],
    ['Tax & legal', [
      [FIELD_LABEL.vat, vendor.vat],
      [FIELD_LABEL.company_registry, vendor.company_registry],
    ]],
    ['Identity', [
      ['Type', vendor.is_company === false ? 'Individual' : 'Company'],
      // No Odoo company means every group company shares the vendor: a fact, not a gap.
      ['Odoo company', vendor.odoo_company_name || 'Shared by all companies'],
    ]],
  ];
  return (
    <section className="vd-panel" aria-labelledby="vd-facts-title">
      <header className="vd-panel-head">
        <h2 id="vd-facts-title" className="vd-panel-title">Details</h2>
      </header>
      <div className="vd-facts">
        {rows.map(([title, facts]) => (
          <div key={title} className="vd-facts-group">
            <h3>{title}</h3>
            <dl className="vd-dl">
              {facts.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt>{label}</dt>
                  <dd dir="auto">{orDash(value)}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  );
}
